import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge doesn't know the custom `--text-*` UI type scale defined in
 * `globals.css` (`text-nano`/`text-micro`/`text-mini`/`text-caption`/`text-compact`),
 * so by default it falls back to treating them as `text-color` utilities — which makes
 * them silently conflict with (and lose to) a later `text-{color}` class in the same
 * `cn()` call instead of coexisting with it as a font-size.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-nano',
        'text-micro',
        'text-mini',
        'text-caption',
        'text-compact',
      ],
    },
  },
});

/**
 * `cn` — class-name composer. Accepts any number of class-name inputs (strings,
 * arrays, objects with boolean values), runs them through `clsx`, then through
 * `tailwind-merge` so conflicting Tailwind utilities resolve last-write-wins.
 *
 * This is the canonical helper for shadcn/ui primitives in this project.
 *
 * @example
 * cn('px-2 py-1', 'px-4', isActive && 'bg-primary')
 * // → 'py-1 px-4 bg-primary'  (px-2 dropped because px-4 came later)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
